import { Effect, Layer, Schema } from "effect";
import { HttpClient, HttpClientRequest, type HttpClientResponse } from "effect/unstable/http";
import { HttpApi, HttpApiClient } from "effect/unstable/httpapi";
import { BrowserObservabilityApi as SharedBrowserApi, BrowserWorkspaceId, browserListFilters } from "@workerstation/shared/observability/browser";
import { TraceApi as SharedTraceApi, TraceScopeId, TraceHttpId, traceListFilters } from "@workerstation/shared/observability/traces";
import { resourceUnavailable } from "@workerstation/shared/errors/resource";
import { BrowserApi, TraceApi } from "./service.ts";
import { BrowserFilters, BrowserContextWindow, TraceFilters, ObservabilityFailure, ObservabilityRequestError } from "./model.ts";

const Api = HttpApi.make("FrontendObservabilityApi").add(SharedBrowserApi).add(SharedTraceApi);
const Envelope = Schema.Struct({ error: Schema.optionalKey(Schema.Json) });
const PublicFailure = Schema.Struct({ error: Schema.String });
const invalid = () => new ObservabilityRequestError({ status: 400, message: "Invalid observability request." });
const decode = <S extends Schema.Top>(schema: S, value: unknown) => Schema.decodeUnknownEffect(schema)(value).pipe(Effect.mapError(invalid));
const own = <A, E, R>(operation: Effect.Effect<A, E, R>) => Effect.scoped(operation.pipe(
  Effect.mapError((error): ObservabilityFailure => Schema.is(ObservabilityFailure)(error) ? error : resourceUnavailable())
));
const refusal = (response: HttpClientResponse.HttpClientResponse): Effect.Effect<never, ObservabilityFailure> => {
  const status = response.status;
  if (status !== 400 && status !== 404 && status !== 405) return Effect.fail(resourceUnavailable());
  return response.json.pipe(Effect.flatMap(Schema.decodeUnknownEffect(PublicFailure)), Effect.mapError(() => resourceUnavailable()),
    Effect.flatMap(body => Effect.fail(new ObservabilityRequestError({ status, message: body.error }))));
};
const makeClient = Effect.fn("Frontend.ObservabilityApi.client")(function* (baseUrl: string) {
  const http = HttpClient.transformResponse(HttpClient.withScope(yield* HttpClient.HttpClient), response => response.pipe(
    Effect.mapError(() => resourceUnavailable()),
    Effect.flatMap(value => value.status >= 200 && value.status < 300 ? value.json.pipe(
      Effect.flatMap(Schema.decodeUnknownEffect(Envelope)), Effect.mapError(() => resourceUnavailable()),
      Effect.flatMap(body => body.error ? Effect.fail(resourceUnavailable()) : Effect.succeed(value))
    ) : refusal(value))
  ));
  const client = yield* HttpApiClient.makeWith(Api, { httpClient: http, baseUrl });
  const queryClient = (query: Readonly<Record<string, string>>) => HttpApiClient.makeWith(Api, {
    httpClient: HttpClient.mapRequest(http, request => HttpClientRequest.setUrlParams(request, query)), baseUrl
  });
  return { client, queryClient };
});
export const makeBrowserApi = Effect.fn("Frontend.BrowserApi.make")(function* (baseUrl: string) {
  const { client, queryClient } = yield* makeClient(baseUrl);
  const list = Effect.fn("Frontend.BrowserApi.request")(function* (workspaceId: string, query: Record<string, string>) {
    const scope = yield* decode(BrowserWorkspaceId, workspaceId);
    yield* browserListFilters(new URLSearchParams(query)).pipe(Effect.mapError(invalid));
    const result = yield* (yield* queryClient(query)).browserObservability.list({ params: { workspaceId: scope }, query });
    if (result.workspaceId !== scope || result.events.some(event => event.workspaceId !== scope || (query.stationId !== undefined && event.stationId !== undefined && event.stationId !== query.stationId))) return yield* Effect.fail(resourceUnavailable());
    return result;
  });
  return BrowserApi.of({
    list: Effect.fn("Frontend.BrowserApi.list")(function* (workspaceId, filters) {
      const input = yield* decode(BrowserFilters, filters);
      const query: Record<string, string> = { limit: "250" };
      if (input.stationId) query.stationId = input.stationId;
      if (input.search?.trim()) query.search = input.search.trim();
      if (input.level) query.level = input.level;
      if (input.category?.trim()) query.category = input.category.trim();
      if (input.sessionId) query.sessionId = input.sessionId;
      return yield* list(workspaceId, query);
    }, own),
    context: Effect.fn("Frontend.BrowserApi.context")(function* (workspaceId, window) {
      const input = yield* decode(BrowserContextWindow, window);
      return yield* list(workspaceId, { sessionId: input.sessionId, since: String(input.since), before: String(input.before), limit: "500" });
    }, own),
    clear: Effect.fn("Frontend.BrowserApi.clear")(function* (workspaceId) {
      const scope = yield* decode(BrowserWorkspaceId, workspaceId);
      const result = yield* client.browserObservability.clear({ params: { workspaceId: scope } });
      if (result.workspaceId !== scope) return yield* Effect.fail(resourceUnavailable());
      return result;
    }, own)
  });
});
export const makeTraceApi = Effect.fn("Frontend.TraceApi.make")(function* (baseUrl: string) {
  const { client, queryClient } = yield* makeClient(baseUrl);
  const station = (stationId: string | undefined) => stationId === undefined ? Effect.succeed({}) : decode(TraceScopeId, stationId).pipe(Effect.map(value => ({ stationId: value })));
  return TraceApi.of({
    list: Effect.fn("Frontend.TraceApi.list")(function* (workspaceId, filters) {
      const scope = yield* decode(TraceScopeId, workspaceId), input = yield* decode(TraceFilters, filters);
      const query: Record<string, string> = { limit: "75", ...(yield* station(input.stationId)) };
      if (input.status) query.status = input.status;
      if (input.operation?.trim()) query.operation = input.operation.trim();
      yield* traceListFilters(new URLSearchParams(query)).pipe(Effect.mapError(invalid));
      const result = yield* (yield* queryClient(query)).traces.list({ params: { workspaceId: scope }, query });
      if (result.workspaceId !== scope || result.traces.some(trace => (trace.workspaceId !== undefined && trace.workspaceId !== scope) || (input.stationId !== undefined && trace.stationId !== undefined && trace.stationId !== input.stationId))) return yield* Effect.fail(resourceUnavailable());
      return result;
    }, own),
    read: Effect.fn("Frontend.TraceApi.read")(function* (workspaceId, traceId, stationId) {
      const scope = yield* decode(TraceScopeId, workspaceId), id = yield* decode(TraceHttpId, traceId), query = yield* station(stationId);
      const result = yield* client.traces.read({ params: { workspaceId: scope, traceId: id }, query });
      if (result.workspaceId !== scope || result.traceId !== id || result.spans.some(span => span.traceId !== id || (span.workspaceId !== undefined && span.workspaceId !== scope) || (stationId !== undefined && span.stationId !== undefined && span.stationId !== stationId))) return yield* Effect.fail(resourceUnavailable());
      return result;
    }, own),
    clear: Effect.fn("Frontend.TraceApi.clear")(function* (workspaceId, stationId) {
      const scope = yield* decode(TraceScopeId, workspaceId);
      const result = yield* client.traces.clear({ params: { workspaceId: scope }, query: yield* station(stationId) });
      if (result.workspaceId !== scope) return yield* Effect.fail(resourceUnavailable());
      return result;
    }, own)
  });
});
export const BrowserApiLive = (baseUrl: string) => Layer.effect(BrowserApi, makeBrowserApi(baseUrl));
export const TraceApiLive = (baseUrl: string) => Layer.effect(TraceApi, makeTraceApi(baseUrl));
export const ObservabilityApisLive = (baseUrl: string) => Layer.merge(BrowserApiLive(baseUrl), TraceApiLive(baseUrl));
